import { useAtomValue } from 'jotai';
import { useCallback } from 'react';

import { logger } from '../../datadog/logger';
import { TrackingEvents, sendLoggingEvents } from '../../events';
import { useAtomCallbackMemoized } from '../../hooks/useAtomCallbackMemoized';
import { unreadMessagesCountFamily } from '../../session/state';
import { toast } from '../../toasts';
import { participantsAtom } from '../../video-sdk';
import { useOpenConversationCallback } from '../chat/useOpenConversationCallback';
import { useOpenClientInformationCallback } from '../client-information';
import { ProviderDrawerState, inSessionPatientInformationAtom, sessionConversationSidAtom } from '../state';
import { ClientsInSession, ClientsInSessionActions } from './ClientsInSession';
import type { ClientsInSessionActionsProps } from './ClientsInSession';
import { useRemoveClientCallback } from './useRemoveClientCallback';

function ClientsInSessionActionsWrapper({
  participantSid,
  clientName,
  visitorUuid,
}: { participantSid: string; visitorUuid: string } & Pick<ClientsInSessionActionsProps, 'clientName'>) {
  const { callback: removeClient, loading } = useRemoveClientCallback();

  const getSessionConversationSid = useAtomCallbackMemoized((get) => get(sessionConversationSidAtom), []);

  const inSessionVisitor = useAtomValue(inSessionPatientInformationAtom);
  const patientShortId = inSessionVisitor?.patientShortId;
  const sessionConversationSid = getSessionConversationSid();

  const unreadMessagesCount = useAtomValue(unreadMessagesCountFamily(sessionConversationSid ?? ''));
  const openConversation = useOpenConversationCallback();
  const openClientInformation = useOpenClientInformationCallback();

  const openClientInformationSection = useCallback(() => {
    if (!patientShortId) {
      logger.error('Missing in-session patient short ID when opening client information drawer', { visitorUuid });
      return;
    }
    openClientInformation({ visitorUuid, patientShortId });
  }, [visitorUuid, patientShortId, openClientInformation]);

  const openSessionConversation = () => {
    if (!sessionConversationSid) {
      logger.error('sessionConversationSid not found', {
        participantSid,
        clientName,
      });
      toast.error('Unable to open chat');
      return;
    }
    openConversation(ProviderDrawerState.SESSION_CHAT, sessionConversationSid);
  };

  return (
    <ClientsInSessionActions
      onRemove={() => {
        sendLoggingEvents(TrackingEvents.PROVIDER_CLIENT_REMOVE);
        removeClient(participantSid);
      }}
      clientName={clientName}
      unreadMessagesCount={unreadMessagesCount}
      onChat={openSessionConversation}
      loading={loading}
      patientShortId={patientShortId}
      onOpenClientInformationDrawer={openClientInformationSection}
    />
  );
}

export function ClientsInSessionWrapper() {
  const participants = useAtomValue(participantsAtom);

  return <ClientsInSession clientActionsComponent={ClientsInSessionActionsWrapper} participants={participants} />;
}
